import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { AdminLayout } from '~/components/AdminLayout'
import { useAllOrders } from '~/api/admin-orders'
import type { Order } from '~/api/orders'
import styles from './orders.module.css'

/**
 * Order status enum matching backend
 */
const ORDER_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'PREPARING', label: 'Preparing' },
  { value: 'READY_FOR_PICKUP', label: 'Ready for Pickup' },
  { value: 'IN_TRANSIT', label: 'In Transit' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CANCELLED', label: 'Cancelled' },
] as const

const PAYMENT_PROVIDERS = [
  { value: '', label: 'All Providers' },
  { value: 'MIDTRANS', label: 'Midtrans' },
  { value: 'XENDIT', label: 'Xendit' },
] as const

/**
 * Sort direction type
 */
type SortDirection = 'asc' | 'desc' | null

/**
 * Sort config type
 */
interface SortConfig {
  key: keyof Order | null
  direction: SortDirection
}

/**
 * Get the CSS class for an order status badge
 */
function getStatusClass(status: string): string {
  switch (status) {
    case 'PENDING':
      return styles.statusPending
    case 'CONFIRMED':
      return styles.statusConfirmed
    case 'PREPARING':
      return styles.statusPreparing
    case 'READY_FOR_PICKUP':
      return styles.statusReadyForPickup
    case 'IN_TRANSIT':
      return styles.statusInTransit
    case 'DELIVERED':
      return styles.statusDelivered
    case 'CANCELLED':
      return styles.statusCancelled
    default:
      return ''
  }
}

/**
 * Get the CSS class for a payment status badge
 */
function getPaymentStatusClass(status: string | undefined): string {
  switch (status) {
    case 'PENDING':
      return styles.paymentPending
    case 'PAID':
      return styles.paymentPaid
    case 'EXPIRED':
      return styles.paymentExpired
    case 'FAILED':
      return styles.paymentFailed
    case 'REFUNDED':
      return styles.paymentRefunded
    default:
      return styles.paymentUnknown
  }
}

/**
 * Format payment status for display
 */
function formatPaymentStatus(status: string | undefined): string {
  if (!status) return '—'
  const labels: Record<string, string> = {
    PENDING: 'UNPAID',
    PAID: 'PAID',
    EXPIRED: 'EXPIRED',
    FAILED: 'FAILED',
    REFUNDED: 'REFUNDED',
  }
  return labels[status] || status.replace(/_/g, ' ')
}

function formatPaymentProvider(provider: string | null | undefined): string {
  if (!provider) return '—'
  return provider.replace(/_/g, ' ')
}

/**
 * Format status for display (replace underscores with spaces)
 */
function formatStatus(status: string): string {
  return status.replace(/_/g, ' ')
}

/**
 * Format amount in IDR currency
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/**
 * Format date to readable string
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Truncate customer ID to first 8 characters
 */
function truncateCustomerId(customerId: string): string {
  return customerId.slice(0, 8)
}

function normalizePhone(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.startsWith('62')) return `0${digits.slice(2)}`
  return digits
}

function getCustomerLabel(order: Order): string {
  if (order.customer) return order.customer.name
  return truncateCustomerId(order.customerId)
}

function getCustomerPhone(order: Order): string {
  if (!order.customer || !order.customer.phone) return '—'
  return order.customer.phone
}

function getOrderTextSearchFields(order: Order): readonly string[] {
  const fields = [order.id, order.externalId, order.customerId]
  if (order.paymentProvider) fields.push(order.paymentProvider)
  if (order.paymentProviderReference) fields.push(order.paymentProviderReference)
  if (order.paymentTransactionId) fields.push(order.paymentTransactionId)
  if (order.customer) {
    fields.push(order.customer.name)
  }
  return fields
}

function getCustomerPhoneSearchFields(order: Order): readonly string[] {
  if (!order.customer || !order.customer.phone) return []
  return [order.customer.phone]
}

/**
 * Admin Orders Page - Data table showing all orders with filtering and sorting
 */
export function AdminOrdersPage() {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [providerFilter, setProviderFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: null })

  // Fetch orders with status filter
  const { data: orders, isLoading, isError, error } = useAllOrders(statusFilter || undefined)

  // Filter orders by search query (client-side)
  const filteredOrders = useMemo(() => {
    if (!orders) return []
    const providerMatched = providerFilter
      ? orders.filter((order) => order.paymentProvider === providerFilter)
      : orders
    if (!searchQuery.trim()) return providerMatched

    const query = searchQuery.toLowerCase()
    const normalizedQuery = normalizePhone(query)
    return providerMatched.filter((order) => {
      const textMatch = getOrderTextSearchFields(order).some((field) => field.toLowerCase().includes(query))
      const phoneMatch = normalizedQuery.length > 0
        && getCustomerPhoneSearchFields(order).some((phone) => normalizePhone(phone).includes(normalizedQuery))
      return textMatch || phoneMatch
    })
  }, [orders, providerFilter, searchQuery])

  // Sort filtered orders
  const sortedOrders = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredOrders

    return [...filteredOrders].sort((a, b) => {
      const aValue = a[sortConfig.key!]
      const bValue = b[sortConfig.key!]

      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      let comparison = 0
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        // For dates, compare as dates
        if (sortConfig.key === 'createdAt' || sortConfig.key === 'updatedAt') {
          comparison = new Date(aValue).getTime() - new Date(bValue).getTime()
        } else {
          comparison = aValue.localeCompare(bValue)
        }
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison
    })
  }, [filteredOrders, sortConfig])

  // Handle row click navigation
  const handleRowClick = useCallback(
    (order: Order) => {
      navigate({
        to: '/admin/orders/$orderId',
        params: { orderId: order.id },
      })
    },
    [navigate]
  )

  // Handle column sort
  const handleSort = useCallback((key: keyof Order) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // Toggle direction: null -> asc -> desc -> null
        if (prev.direction === null) return { key, direction: 'asc' }
        if (prev.direction === 'asc') return { key, direction: 'desc' }
        return { key: null, direction: null }
      }
      return { key, direction: 'asc' }
    })
  }, [])

  // Handle status filter change
  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value)
  }, [])

  const handleProviderChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setProviderFilter(e.target.value)
  }, [])

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }, [])

  // Get sort indicator
  const getSortIndicator = (key: keyof Order) => {
    if (sortConfig.key !== key) return '↕'
    if (sortConfig.direction === 'asc') return '↑'
    if (sortConfig.direction === 'desc') return '↓'
    return '↕'
  }

  // Check if column is sorted
  const isSorted = (key: keyof Order) => sortConfig.key === key && sortConfig.direction !== null

  if (isLoading) {
    return (
      <AdminLayout title="ORDERS">
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>Loading orders...</span>
        </div>
      </AdminLayout>
    )
  }

  if (isError) {
    return (
      <AdminLayout title="ORDERS">
        <div className={styles.errorContainer}>
          <span className={styles.errorText}>
            Error: {error?.message || 'Failed to load orders'}
          </span>
        </div>
      </AdminLayout>
    )
  }

  const hasOrders = sortedOrders.length > 0
  const showEmptySearch = orders && orders.length > 0 && sortedOrders.length === 0

  return (
    <AdminLayout title="ORDERS">
      <div className={styles.ordersPage}>
        {/* Controls */}
        <div className={styles.controls}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search ID / provider ref / name / phone..."
            value={searchQuery}
            onChange={handleSearchChange}
            aria-label="Search by order ID, provider reference, customer name, or phone"
          />
          <select
            className={styles.filterSelect}
            value={statusFilter}
            onChange={handleStatusChange}
            aria-label="Filter by status"
          >
            {ORDER_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
          <select
            className={styles.filterSelect}
            value={providerFilter}
            onChange={handleProviderChange}
            aria-label="Filter by payment provider"
          >
            {PAYMENT_PROVIDERS.map((provider) => (
              <option key={provider.value} value={provider.value}>
                {provider.label}
              </option>
            ))}
          </select>
          {orders && (
            <span className={styles.resultsCount}>
              {sortedOrders.length} of {orders.length} orders
            </span>
          )}
        </div>

        {/* Table or Empty State */}
        {!hasOrders ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyText}>
              {showEmptySearch ? 'No orders found matching your search' : 'No orders yet'}
            </span>
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>ORDER ID</th>
                  <th className={styles.th}>STATUS</th>
                  <th className={styles.th}>PAYMENT</th>
                  <th className={styles.th}>PROVIDER</th>
                  <th
                    className={`${styles.th} ${styles.thSortable}`}
                    onClick={() => handleSort('totalAmount')}
                    data-sorted={isSorted('totalAmount')}
                  >
                    TOTAL
                    <span className={styles.sortIcon}>{getSortIndicator('totalAmount')}</span>
                  </th>
                  <th className={styles.th}>CUSTOMER</th>
                  <th className={styles.th}>PHONE</th>
                  <th className={styles.th}>ADDRESS</th>
                  <th
                    className={`${styles.th} ${styles.thSortable}`}
                    onClick={() => handleSort('createdAt')}
                    data-sorted={isSorted('createdAt')}
                  >
                    CREATED
                    <span className={styles.sortIcon}>{getSortIndicator('createdAt')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedOrders.map((order) => (
                  <tr
                    key={order.id}
                    className={styles.tr}
                    onClick={() => handleRowClick(order)}
                  >
                    <td className={styles.td}>
                      <span className={styles.orderId}>{order.externalId}</span>
                    </td>
                    <td className={styles.td}>
                      <span className={`${styles.statusBadge} ${getStatusClass(order.status)}`}>
                        {formatStatus(order.status)}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <span className={`${styles.paymentBadge} ${getPaymentStatusClass(order.paymentStatus ?? undefined)}`}>
                        {formatPaymentStatus(order.paymentStatus ?? undefined)}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.providerBadge}>
                        {formatPaymentProvider(order.paymentProvider)}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.amount}>{formatCurrency(order.totalAmount)}</span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.customerId}>
                        {getCustomerLabel(order)}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.phone}>
                        {getCustomerPhone(order)}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.address} title={order.deliveryAddress}>
                        {order.deliveryAddress}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.date}>{formatDate(order.createdAt)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
